import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  Link2, Check, X, Clock, ExternalLink, Loader2, RefreshCw,
  Activity, Watch, Heart, Apple, BarChart3
} from 'lucide-react';
import {
  getIntegrationsStatus, getGarminStatus, initiateStravaConnect,
  initiateGarminConnect, disconnectStrava, disconnectGarmin, syncStravaData,
  syncAppleHealthActivities, setAppleHealthConnected,
} from '@/lib/api';
import type { AppleHealthSyncPayload } from '@/lib/api';
import { isHealthKitPlatform, requestHealthKitAuth, fetchHealthKitWorkouts, type HealthKitAuthResult } from '@/lib/healthkit';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { useQueryClient } from '@tanstack/react-query';

interface Integration {
  id: string;
  nameKey: string;
  descKey: string;
  icon: React.ElementType;
  status: 'connected' | 'disconnected' | 'coming-soon';
  primary?: boolean;
  lastSync?: string;
  historical_backfill_complete?: boolean;
  historical_backfill_cursor_date?: string | null;
  garminStatusDetail?: { status: string | null; last_webhook_at: string | null } | null;
  hidden?: boolean;
}

const integrationsDef: Integration[] = [
  { id: 'garmin', nameKey: 'settings.integrations.garminName', descKey: 'settings.integrations.garminDesc', icon: Watch, status: 'disconnected', primary: true },
  { id: 'strava', nameKey: 'settings.integrations.stravaName', descKey: 'settings.integrations.stravaDesc', icon: Activity, status: 'disconnected' },
  { id: 'apple-health', nameKey: 'settings.integrations.appleHealthName', descKey: 'settings.integrations.appleHealthDesc', icon: Apple, status: 'disconnected', hidden: !isHealthKitPlatform() },
  { id: 'whoop', nameKey: 'settings.integrations.whoopName', descKey: 'settings.integrations.whoopDesc', icon: Heart, status: 'coming-soon' },
  { id: 'trainingpeaks', nameKey: 'settings.integrations.trainingpeaksName', descKey: 'settings.integrations.trainingpeaksDesc', icon: BarChart3, status: 'coming-soon' },
];

function formatLastSyncTime(
  syncTime: string | null,
  labels: { never: string; justNow: string; minsAgo: (n: number) => string; hoursAgo: (n: number) => string; daysAgo: (n: number) => string }
): string {
  if (!syncTime) return labels.never;
  const date = new Date(syncTime);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return labels.justNow;
  if (diffMins < 60) return labels.minsAgo(diffMins);
  if (diffHours < 24) return labels.hoursAgo(diffHours);
  if (diffDays < 7) return labels.daysAgo(diffDays);
  return date.toLocaleDateString();
}

export function IntegrationsSection() {
  const { t } = useTranslation();
  const { user, status } = useAuth();
  const queryClient = useQueryClient();

  const syncLabels = {
    never: t('settings.integrations.syncNever'),
    justNow: t('settings.integrations.syncJustNow'),
    minsAgo: (n: number) => t('settings.integrations.syncMinsAgo', { count: n }),
    hoursAgo: (n: number) => t('settings.integrations.syncHoursAgo', { count: n }),
    daysAgo: (n: number) => t('settings.integrations.syncDaysAgo', { count: n }),
  };
  const [connectingId, setConnectingId] = useState<string | null>(null);
  const [stravaStatus, setStravaStatus] = useState<{ connected: boolean } | null>(null);
  const [garminStatus, setGarminStatus] = useState<{ connected: boolean; last_sync_at: string | null; historical_backfill_complete?: boolean; historical_backfill_cursor_date?: string | null } | null>(null);
  const [garminStatusDetail, setGarminStatusDetail] = useState<{ status: string | null; last_webhook_at: string | null } | null>(null);
  const [appleHealthStatus, setAppleHealthStatus] = useState<{ connected: boolean; last_sync_at: string | null }>({ connected: false, last_sync_at: null });
  const [syncingAppleHealth, setSyncingAppleHealth] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [syncingStrava, setSyncingStrava] = useState(false);
  const [lastSync, setLastSync] = useState<string | null>(null);

  useEffect(() => {
    if (status !== 'authenticated' || !user) return;
    loadIntegrationStatus();
  }, [status, user]);

  const loadIntegrationStatus = async () => {
    setIsLoading(true);
    try {
      const integrationsStatus = await getIntegrationsStatus();
      const stravaIntegration = integrationsStatus.integrations.find((i: { provider: string }) => i.provider === 'strava');
      setStravaStatus({ connected: stravaIntegration?.connected ?? false });
      const garminIntegration = integrationsStatus.integrations.find((i: { provider: string }) => i.provider === 'garmin');
      const garminConnected = garminIntegration?.connected ?? false;
      setGarminStatus({
        connected: garminConnected,
        last_sync_at: garminIntegration?.last_sync_at ?? null,
        historical_backfill_complete: (garminIntegration as Record<string, unknown>)?.historical_backfill_complete as boolean ?? false,
        historical_backfill_cursor_date: (garminIntegration as Record<string, unknown>)?.historical_backfill_cursor_date as string | null ?? null,
      });
      if (garminConnected) {
        try {
          const detail = await getGarminStatus();
          setGarminStatusDetail({ status: detail.status ?? null, last_webhook_at: detail.last_webhook_at ?? null });
        } catch { setGarminStatusDetail(null); }
      } else { setGarminStatusDetail(null); }

      // Apple Health
      const ahIntegration = integrationsStatus.integrations.find((i: { provider: string }) => i.provider === 'apple_health');
      setAppleHealthStatus({ connected: ahIntegration?.connected ?? false, last_sync_at: ahIntegration?.last_sync_at ?? null });

      const storedLastSync = localStorage.getItem('strava_last_sync');
      if (storedLastSync) setLastSync(storedLastSync);
    } catch {
      setStravaStatus({ connected: false });
      setGarminStatus({ connected: false, last_sync_at: null });
      setAppleHealthStatus({ connected: false, last_sync_at: null });
    } finally { setIsLoading(false); }
  };

  const handleConnect = async (id: string) => {
    if (id === 'strava') {
      setConnectingId(id);
      try { await initiateStravaConnect(); } catch (error) {
        toast({ title: t('settings.integrations.connectStravaError'), description: error instanceof Error ? error.message : t('settings.integrations.connectError'), variant: 'destructive' });
        setConnectingId(null);
      }
    } else if (id === 'garmin') {
      setConnectingId(id);
      try { await initiateGarminConnect(); } catch (error) {
        if (error instanceof Error && error.message === 'EMAIL_REQUIRED') {
          toast({ title: t('settings.integrations.connectGarminEmailRequired'), description: t('settings.integrations.connectGarminEmailDesc'), variant: 'destructive' });
        } else {
          toast({ title: t('settings.integrations.connectGarminError'), description: error instanceof Error ? error.message : t('settings.integrations.connectError'), variant: 'destructive' });
        }
        setConnectingId(null);
      }
    } else if (id === 'apple-health') {
      setConnectingId(id);
      try {
        const result: HealthKitAuthResult = await requestHealthKitAuth();
        if (!result.authorized) {
          // Show actionable error based on the specific failure reason
          const bridgeDetail = result.errorMessage ? ` (${result.errorMessage})` : '';
          const description = result.error === 'healthkit_unavailable'
            ? t('settings.integrations.appleHealthUnavailable', { defaultValue: 'HealthKit is not available on this device. Make sure Health is enabled in Settings.' })
            : result.error === 'bridge_error'
            ? `${t('settings.integrations.appleHealthBridgeError', { defaultValue: 'Could not connect to HealthKit. Please restart the app and try again.' })}${bridgeDetail}`
            : result.error === 'authorization_error'
            ? result.errorMessage ?? t('settings.integrations.appleHealthAuthError', { defaultValue: 'HealthKit authorization failed. Check Settings > Privacy & Security > Health to grant access.' })
            : `${t('settings.integrations.appleHealthSystemError', { defaultValue: 'HealthKit authorization failed. Check Settings > Privacy & Security > Health to grant access.' })}${bridgeDetail}`;
          toast({ title: t('settings.integrations.appleHealthPermissionDenied'), description, variant: 'destructive' });
          setConnectingId(null);
          return;
        }
        await setAppleHealthConnected(true);
        setAppleHealthStatus(prev => ({ ...prev, connected: true }));
        toast({ title: t('settings.integrations.appleHealthConnected') });
        // Trigger initial sync (last 30 days)
        await handleSyncAppleHealth();
      } catch (error) {
        toast({ title: t('settings.integrations.connectError'), description: error instanceof Error ? error.message : undefined, variant: 'destructive' });
      } finally { setConnectingId(null); }
    } else {
      toast({ title: t('settings.integrations.comingSoonTitle'), description: t('settings.integrations.comingSoonDesc') });
      return;
    }
    // Update onboarding progress card
    queryClient.invalidateQueries({ queryKey: ['integrations', 'status'] });
    queryClient.invalidateQueries({ queryKey: ['profile', 'completeness'] });
  };

  const handleDisconnect = async (id: string) => {
    setConnectingId(id);
    try {
      if (id === 'strava') { await disconnectStrava(); setStravaStatus({ connected: false }); }
      else if (id === 'garmin') { await disconnectGarmin(); setGarminStatus({ connected: false, last_sync_at: null }); setGarminStatusDetail(null); queryClient.invalidateQueries({ queryKey: ['integrations', 'status'] }); await loadIntegrationStatus(); }
      else if (id === 'apple-health') { await setAppleHealthConnected(false); setAppleHealthStatus({ connected: false, last_sync_at: null }); }
      const disconnectedKey = id === 'garmin' ? 'settings.integrations.garminDisconnected'
        : id === 'apple-health' ? 'settings.integrations.appleHealthDisconnected'
        : 'settings.integrations.stravaDisconnected';
      toast({ title: t(disconnectedKey) });
      queryClient.invalidateQueries({ queryKey: ['integrations', 'status'] });
      queryClient.invalidateQueries({ queryKey: ['profile', 'completeness'] });
    } catch (error) {
      const apiError = error as { status?: number };
      if (id === 'strava' && apiError.status && apiError.status < 500) { setStravaStatus({ connected: false }); toast({ title: t('settings.integrations.stravaDisconnected') }); }
      else { toast({ title: t('settings.integrations.disconnectError'), description: error instanceof Error ? error.message : t('settings.integrations.disconnectErrorDesc'), variant: 'destructive' }); }
    } finally { setConnectingId(null); }
  };

  const handleSyncStrava = async () => {
    setSyncingStrava(true);
    try {
      await syncStravaData();
      const now = new Date().toISOString();
      localStorage.setItem('strava_last_sync', now);
      setLastSync(now);
      toast({ title: t('settings.integrations.syncStarted'), description: t('settings.integrations.syncStartedDesc') });
    } catch (error) {
      toast({ title: t('settings.integrations.syncFailed'), description: error instanceof Error ? error.message : t('settings.integrations.syncFailedDesc'), variant: 'destructive' });
    } finally { setSyncingStrava(false); }
  };

  const handleSyncAppleHealth = async () => {
    setSyncingAppleHealth(true);
    try {
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const workouts = await fetchHealthKitWorkouts(thirtyDaysAgo, now);

      if (workouts.length === 0) {
        toast({ title: t('settings.integrations.appleHealthSyncComplete'), description: t('settings.integrations.appleHealthNoWorkouts') });
        setSyncingAppleHealth(false);
        return;
      }

      const payload: AppleHealthSyncPayload[] = workouts.map(w => ({
        id: w.id,
        sport: w.sport,
        start_date: w.startDate,
        end_date: w.endDate,
        duration_seconds: w.durationSeconds,
        distance_meters: w.distanceMeters ?? null,
        calories: w.calories ?? null,
        avg_heart_rate: w.avgHeartRate ?? null,
        max_heart_rate: w.maxHeartRate ?? null,
        source_name: w.sourceName ?? null,
        source_bundle_id: w.sourceBundleId ?? null,
      }));

      const result = await syncAppleHealthActivities(payload);
      setAppleHealthStatus(prev => ({ ...prev, last_sync_at: new Date().toISOString() }));
      toast({
        title: t('settings.integrations.appleHealthSyncComplete'),
        description: t('settings.integrations.appleHealthImported', { count: result.imported }),
      });
    } catch (error) {
      toast({ title: t('settings.integrations.appleHealthSyncFailed'), description: error instanceof Error ? error.message : undefined, variant: 'destructive' });
    } finally { setSyncingAppleHealth(false); }
  };

  const updatedIntegrations: Integration[] = integrationsDef.map(i => {
    if (i.id === 'strava') return { ...i, status: stravaStatus?.connected ? 'connected' as const : 'disconnected' as const, lastSync: stravaStatus?.connected && lastSync ? lastSync : undefined };
    if (i.id === 'garmin') return { ...i, status: garminStatus?.connected ? 'connected' as const : 'disconnected' as const, lastSync: garminStatus?.last_sync_at ? formatLastSyncTime(garminStatus.last_sync_at, syncLabels) : undefined, historical_backfill_complete: garminStatus?.historical_backfill_complete, historical_backfill_cursor_date: garminStatus?.historical_backfill_cursor_date, garminStatusDetail: garminStatus?.connected ? garminStatusDetail : undefined };
    if (i.id === 'apple-health') return { ...i, status: appleHealthStatus.connected ? 'connected' as const : 'disconnected' as const, lastSync: appleHealthStatus.last_sync_at ? formatLastSyncTime(appleHealthStatus.last_sync_at, syncLabels) : undefined };
    return i;
  });

  return (
    <div id="integrations" className="rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[16px] p-5 shadow-[inset_0_1px_0_rgba(0,255,200,0.04)] scroll-mt-24">
      <div className="flex items-center gap-2.5 mb-6">
        <div className="p-1.5 rounded-md border border-[#00FFC8]/20 bg-[#00FFC8]/5">
          <Link2 className="h-4 w-4 text-[#00FFC8]" />
        </div>
        <div>
          <h3 className="text-sm font-medium text-foreground">{t('settings.integrations.title')}</h3>
          <p className="text-[9px] font-mono tracking-[0.15em] uppercase text-muted-foreground">{t('settings.integrations.subtitle')}</p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="space-y-2">
          {updatedIntegrations.filter(i => !i.hidden).map((integration) => {
            const Icon = integration.icon;
            const isComingSoon = integration.status === 'coming-soon';
            const isConnected = integration.status === 'connected';
            const isLoadingThis = connectingId === integration.id;
            const isSyncing = (syncingStrava && integration.id === 'strava') || (syncingAppleHealth && integration.id === 'apple-health');

            return (
              <div
                key={integration.id}
                className={cn(
                  'flex flex-col gap-2 px-3 py-3 rounded-md border transition-colors',
                  isConnected ? 'border-[#00FFC8]/20 bg-[#00FFC8]/[0.03]' : 'border-white/[0.04] bg-white/[0.01]',
                  isComingSoon && 'opacity-40'
                )}
              >
                <div className="flex items-start gap-3">
                  <Icon className={cn('h-4 w-4 shrink-0 mt-0.5', isConnected ? 'text-[#00FFC8]' : 'text-white/30')} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] font-mono tracking-wider text-foreground whitespace-nowrap">{t(integration.nameKey)}</span>
                    {integration.primary && isConnected && (
                      <span className="text-[8px] font-mono tracking-wider text-[#00FFC8] bg-[#00FFC8]/10 px-1.5 py-0.5 rounded">{t('settings.integrations.primary')}</span>
                    )}
                    {isComingSoon && (
                      <span className="text-[8px] font-mono tracking-wider text-white/30 bg-white/[0.04] px-1.5 py-0.5 rounded">{t('settings.integrations.soon')}</span>
                    )}
                  </div>
                  <p className="text-[9px] font-mono text-[#A0A0A0] mt-0.5">{t(integration.descKey)}</p>
                  {integration.id === 'apple-health' && (
                    <div className="mt-1 space-y-1">
                      <p className="text-[8px] font-mono text-white/25">{t('settings.integrations.appleHealthDataInfo')}</p>
                      <div className="flex flex-wrap gap-1">
                        {['Workouts', 'Heart Rate', 'Active Energy', 'Distance'].map((type) => (
                          <span key={type} className="text-[7px] font-mono tracking-wider uppercase px-1.5 py-0.5 rounded bg-[#FF2D55]/10 text-[#FF2D55]/60 border border-[#FF2D55]/15">
                            {type}
                          </span>
                        ))}
                      </div>
                      <p className="text-[7px] font-mono text-white/20 tracking-wider">
                        Read-only access via Apple HealthKit. Data synced to AthleteSpace for training insights. No data is written to Apple Health.
                      </p>
                    </div>
                  )}
                  {isConnected && integration.lastSync && (
                    <div className="flex items-center gap-1 mt-1 text-[9px] font-mono text-white/30">
                      <Clock className="h-2.5 w-2.5" />
                      <span>{t('settings.integrations.lastSync')}{integration.lastSync}</span>
                    </div>
                  )}
                  {isConnected && integration.id === 'garmin' && integration.historical_backfill_cursor_date && !integration.historical_backfill_complete && (
                    <div className="flex items-center gap-1 mt-1 text-[9px] font-mono text-[hsl(35,85%,55%)]">
                      <Loader2 className="h-2.5 w-2.5 animate-spin" />
                      <span>{t('settings.integrations.backfillInProgress')}{new Date(integration.historical_backfill_cursor_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap pl-7">
                  {isConnected && (
                    <div className="flex items-center gap-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#00FFC8] shadow-[0_0_6px_rgba(0,255,200,0.6)]" />
                      <span className="text-[9px] font-mono tracking-wider text-[#00FFC8]">{t('settings.integrations.active')}</span>
                    </div>
                  )}

                  {isConnected && (integration.id === 'strava' || integration.id === 'apple-health') && (
                    <button
                      onClick={() => integration.id === 'strava' ? handleSyncStrava() : handleSyncAppleHealth()}
                      disabled={isSyncing}
                      className="px-2 py-1 rounded border border-white/[0.08] bg-white/[0.03] text-[9px] font-mono tracking-wider text-muted-foreground hover:text-[#00FFC8] hover:border-[#00FFC8]/30 transition-colors disabled:opacity-40"
                    >
                      {isSyncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                    </button>
                  )}

                  {!isComingSoon && (
                    <button
                      onClick={() => isConnected ? handleDisconnect(integration.id) : handleConnect(integration.id)}
                      disabled={isLoadingThis || isSyncing}
                      className={cn(
                        'px-2.5 py-1 rounded border text-[9px] font-mono tracking-wider transition-all',
                        isConnected
                          ? 'border-white/[0.08] bg-white/[0.03] text-white/40 hover:text-destructive hover:border-destructive/30'
                          : 'border-[#00FFC8]/25 bg-[#00FFC8]/[0.06] text-[#00FFC8] hover:bg-[#00FFC8]/10',
                        (isLoadingThis || isSyncing) && 'opacity-40 pointer-events-none'
                      )}
                    >
                      {isLoadingThis ? <Loader2 className="h-3 w-3 animate-spin" /> : isConnected ? t('settings.integrations.disconnect') : t('settings.integrations.connect')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
